import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import useAccountQuery from 'api/queries/useAccountQuery';
import useFeatureRecordQuery, { FEATURE_KEYS, shouldSeeFeature } from 'api/queries/useFeatureRecordQuery';
import useGiftWithPurchaseItemsQuery from 'api/queries/useGiftWithPurchaseItemsQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import useGlobalStore from 'hooks/useGlobalStore';
import { isWithinNewUserSignupWindow } from 'utils/dateUtils';

import { getSortedInStockGiftWithPurchaseItems } from './giftWithPurchaseUtils';

/**
 * `sessionStorage` key: user closed the GWP modal without adding a gift this session.
 * Suppresses the modal until the tab/session ends; a new session may show it again if still eligible.
 */
const GWP_MODAL_SESSION_DISMISSED_KEY = 'gwpModalDismissedSession';

/**
 * Drives open/close state for {@link GiftWithPurchaseModal} when rendered from the shop layout.
 *
 * **Base eligibility**
 * - `account.canRedeemGWP === true` from the account endpoint.
 * - GWP catalog call succeeded, has in-stock offerable items (after sort/filter).
 * - No free GWP line item already in the cart (`isGwpFree` with `qty > 0`) — treat as claimed.
 *
 * **Onboarding vs GWP timing**
 * - While the user is still due for the **open-window onboarding** tour (`shouldSeeFeature` for
 *   `onboarding_modal_viewed`) and the marketplace is open, or while that modal is visibly open
 *   (`openWindowOnboardingModalOpen`), do **not** show GWP — onboarding goes first.
 * - Always defer GWP on the **featured home** route (`/shop` index), regardless of whether the user
 *   has an `onboarding_modal_viewed` stamp. On any other shop URL (aisle, search, brand, etc.), GWP
 *   may show. That matches “never on Featured; show after leaving Featured.”
 *
 * **Session dismissal**
 * - At most one meaningful prompt per session: closing the modal calls `onCloseGiftWithPurchaseModal`,
 *   which persists dismissal in `sessionStorage` so the modal stays closed until the session ends.
 *
 * **Loading gates** — Waits for feature record and subscription so onboarding flags and signup date
 * are stable before evaluating visibility.
 *
 * @returns {object} Modal wiring for `ShopLayout`.
 * @returns {boolean} return.giftWithPurchaseModalOpen - Whether the modal should be open given the rules above.
 * @returns {() => void} return.onCloseGiftWithPurchaseModal - Marks session dismissal and closes the modal;
 *   call when the user dismisses without claiming.
 */
function useGiftWithPurchaseModal() {
    const location = useLocation();
    const { data: account = {} } = useAccountQuery();
    const { data: subscription, isLoading: isSubscriptionLoading } = useSubscriptionQuery();
    const { data: featureRecord, isLoading: isFeatureRecordLoading } = useFeatureRecordQuery();
    const { data: selections = {} } = useSelectionsQuery();
    const {
        data: giftItems = [],
        isError: isGwpItemsError,
        isLoading: isGwpItemsLoading,
    } = useGiftWithPurchaseItemsQuery();
    const { openWindowOnboardingModalOpen } = useGlobalStore();

    const [gwpModalDismissedThisSession, setGwpModalDismissedThisSession] = useState(
        () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem(GWP_MODAL_SESSION_DISMISSED_KEY) === '1'
    );
    const [giftWithPurchaseModalOpen, setGiftWithPurchaseModalOpen] = useState(false);

    const subscriptionSignupDate = subscription?.createdAt?.date;

    const hasGwpInCart = useMemo(() => {
        const selected = selections?.selected ?? [];
        return selected.some((item) => item.isGwpFree && item.qty > 0);
    }, [selections]);

    const sortedGwpItems = useMemo(() => getSortedInStockGiftWithPurchaseItems(giftItems), [giftItems]);

    const hasOfferableGwpItems = !isGwpItemsLoading && !isGwpItemsError && sortedGwpItems.length > 0;

    const onboardingGatesReady = !isFeatureRecordLoading && !isSubscriptionLoading;

    const shouldShowGiftWithPurchaseModal = useMemo(() => {
        if (gwpModalDismissedThisSession) return false;
        if (!onboardingGatesReady) return false;
        if (account?.canRedeemGWP !== true) return false;
        if (!hasOfferableGwpItems || hasGwpInCart) return false;

        const onboardingEligible = isWithinNewUserSignupWindow({
            numOrders: account?.numOrders,
            signupDate: subscriptionSignupDate,
        });

        const pendingOpenWindowOnboarding =
            account?.marketplaceOpen &&
            onboardingEligible &&
            shouldSeeFeature(featureRecord, FEATURE_KEYS.ONBOARDING_OPEN_MODAL_VIEWED);

        if (pendingOpenWindowOnboarding || openWindowOnboardingModalOpen) return false;

        const normalizedPath = location.pathname.replace(/\/$/, '') || '/';
        const isFeaturedHome = normalizedPath === '/shop';

        // Never show on Featured; eligible users see it after navigating away.
        if (isFeaturedHome) return false;

        return true;
    }, [
        account?.canRedeemGWP,
        account?.marketplaceOpen,
        account?.numOrders,
        featureRecord,
        gwpModalDismissedThisSession,
        hasGwpInCart,
        hasOfferableGwpItems,
        location.pathname,
        onboardingGatesReady,
        openWindowOnboardingModalOpen,
        subscriptionSignupDate,
    ]);

    useEffect(() => {
        setGiftWithPurchaseModalOpen(shouldShowGiftWithPurchaseModal);
    }, [shouldShowGiftWithPurchaseModal]);

    const onCloseGiftWithPurchaseModal = useCallback(() => {
        sessionStorage.setItem(GWP_MODAL_SESSION_DISMISSED_KEY, '1');
        setGwpModalDismissedThisSession(true);
        setGiftWithPurchaseModalOpen(false);
    }, []);

    return {
        giftWithPurchaseModalOpen,
        onCloseGiftWithPurchaseModal,
    };
}

export default useGiftWithPurchaseModal;
